import { useState } from "react";
import { StatusBadge } from "@/components/Shared";
import JobLink from "@/components/JobLink";
import { SubTabs } from "@/components/stores/storesUi";

// Field names as people know them from the sheets.
const FIELD_LABEL = {
  status: "Status", customer_name: "Customer", product_name: "Product", product_code: "Product code",
  po_number: "PO number", po_date: "PO date", quantity: "Order qty", unit: "Unit", due_date: "Due date",
  date_received: "Order received", bpr_issue_date: "BPR issue date", dispatch_date: "Dispatch date",
  invoice_number: "Invoice no.", invoice_date: "Invoice date", notes: "Remarks", job_type: "Job type",
  sap_code: "SAP code", old_code: "Old code", location: "Location", dispatch_qty: "Dispatched qty",
  printing_date: "Printing date", packing_date: "Packing date", slitting_date: "Slitting date",
  not_in_pipeline: "Not in Jobs Pipeline", bpr_status: "BPR status",
};
const label = (f) => FIELD_LABEL[f] || f.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const fmt = (v) => (v === "" || v == null ? "—" : typeof v === "boolean" ? (v ? "Yes" : "No") : typeof v === "number" ? v.toLocaleString("en-IN") : String(v));

/**
 * What a job-register import will do (preview) or did (result): new jobs, changes to
 * existing ones, ERP values kept, BPR clashes and skipped rows. `plan` is the `jobs`
 * block returned by /api/import/* (backend/routes/job_import.py).
 */
export default function JobImportPlan({ plan, done = false, readElsewhere = [] }) {
  const c = plan.counts;
  const lists = [
    { value: "updated", label: `Changes · ${c.updated.toLocaleString("en-IN")}`, n: c.updated },
    { value: "new", label: `New jobs · ${c.new.toLocaleString("en-IN")}`, n: c.new },
    { value: "kept", label: `ERP value kept · ${plan.kept.length.toLocaleString("en-IN")}`, n: plan.kept.length },
    { value: "clashes", label: `BPR clashes · ${c.clashes}`, n: c.clashes },
    { value: "skipped", label: `Skipped rows · ${c.skipped_rows.toLocaleString("en-IN")}`, n: c.skipped_rows },
  ].filter((l) => l.n > 0);
  const [picked, setView] = useState(null);
  const view = lists.some((l) => l.value === picked) ? picked : lists[0]?.value;
  const used = plan.sheets.filter((s) => s.used);
  // Sheets another part of the same import reads (Production, Material Issue, Quality) are not "left alone".
  const unused = plan.sheets.filter((s) => !s.used && !readElsewhere.includes(s.sheet));

  return (
    <div data-testid="job-import-plan">
      <div className="p-5 border-b border-neutral-200 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Tile label={done ? "Created" : "New jobs"} value={c.new} sub={c.new_without_bpr ? `${c.new_without_bpr.toLocaleString("en-IN")} not converted to a BPR yet` : null} tone="text-emerald-700" testid="plan-new" />
        <Tile label={done ? "Updated" : "Will update"} value={c.updated} sub={c.fields_changed ? `${c.fields_changed.toLocaleString("en-IN")} field${c.fields_changed === 1 ? "" : "s"}` : null} tone="text-blue-700" testid="plan-updated" />
        <Tile label="Already up to date" value={c.unchanged} testid="plan-unchanged" />
        <Tile label="ERP value kept" value={c.kept} tone={c.kept ? "text-amber-700" : ""} sub={c.kept ? (c.kept === 1 ? "job" : "jobs") : null} testid="plan-kept" />
        <Tile label="BPR clashes" value={c.clashes} tone={c.clashes ? "text-rose-700" : ""} testid="plan-clashes" />
        <Tile label="Rows skipped" value={c.skipped_rows} testid="plan-skipped" />
      </div>

      <div className="px-5 py-3 border-b border-neutral-200 text-xs text-neutral-600 space-y-1.5">
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="label-caps text-[10px] mr-1">Read</span>
          {used.map((s) => (
            <span key={s.sheet} className="px-2 py-0.5 border border-neutral-200 rounded-sm bg-neutral-50">
              {s.sheet} · <span className="font-mono-num">{s.rows.toLocaleString("en-IN")}</span> rows · <span className="font-mono-num">{(s.bprs || 0).toLocaleString("en-IN")}</span> BPRs
              {s.kind === "pipeline" ? (
                <>
                  {s.repeats > 0 && (
                    <span className="text-neutral-500" title="The same BPR on several rows (front and back label, two POs): each row is its own job, the extra ones numbered MPWO-…(A), (B)">
                      {" "}· {s.repeats.toLocaleString("en-IN")} more rows sharing a BPR, kept as separate jobs
                    </span>
                  )}
                  {s.no_bpr > 0 && (
                    <span className="text-neutral-500"> · {s.no_bpr.toLocaleString("en-IN")} not converted to a BPR yet</span>
                  )}
                </>
              ) : s.repeats > 0 && (
                <span className="text-neutral-500" title="Rows giving a BPR already met in this sheet are merged into one job; the later row wins">
                  {" "}· {s.repeats.toLocaleString("en-IN")} repeated rows merged
                </span>
              )}
            </span>
          ))}
          {readElsewhere.length > 0 && (
            <span className="text-neutral-500">· {readElsewhere.join(", ")}: stage records, below</span>
          )}
          {unused.length > 0 && (
            <span className="text-neutral-400" data-testid="plan-unused-sheets">
              · left alone, no place in the ERP for them yet: {unused.map((s) => s.sheet).join(", ")}
            </span>
          )}
        </div>
        <p>
          Every Jobs Pipeline row is a job, with its Status exactly as the sheet has it; Planning fills in the BPRs converted
          from it, and BPR Status, FG and Dispatch only update jobs already there.
          {c.linked > 0 && ` ${c.linked.toLocaleString("en-IN")} Planning BPRs were matched to their Pipeline row.`}
          {c.merged > 0 && ` ${c.merged.toLocaleString("en-IN")} jobs were the same order as a Pipeline row and are merged into it.`}
          {c.not_in_pipeline > 0 && ` ${c.not_in_pipeline.toLocaleString("en-IN")} Planning BPRs have no row in the Jobs Pipeline: they stay as jobs, tagged "Not in Pipeline", and are left out of the job count.`}
          {c.left_pipeline > 0 && ` ${c.left_pipeline.toLocaleString("en-IN")} jobs are no longer anywhere in this workbook and get the same tag.`}
          {" "}Rows are matched by BPR number (a row not converted yet by customer, PO and item), so re-importing never creates a second job.
          Blank, NA and #N/A cells never clear ERP data, and a value changed in the ERP since the last import is kept.
        </p>
      </div>

      {lists.length > 0 && (
        <div className="p-5 space-y-3">
          <SubTabs value={view} onChange={setView} options={lists} testid="plan-view" />
          {view === "updated" && <ChangesTable rows={plan.updated} total={c.updated} />}
          {view === "new" && <NewTable rows={plan.new} total={c.new} />}
          {view === "kept" && <KeptTable rows={plan.kept} />}
          {view === "clashes" && <ClashTable rows={plan.clashes} total={c.clashes} />}
          {view === "skipped" && <SkippedTable skipped={plan.skipped} total={c.skipped_rows} />}
        </div>
      )}
    </div>
  );
}

function Tile({ label: text, value, sub, tone = "", testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-caps text-[10px]">{text}</div>
      <div className={`font-mono-num text-xl font-bold mt-1 ${tone || "text-neutral-900"}`}>{(value || 0).toLocaleString("en-IN")}</div>
      {sub && <div className="text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}

function Shown({ shown, total }) {
  if (total <= shown) return null;
  return <div className="px-3 py-2 text-[11px] text-neutral-500 border-t border-neutral-100">First {shown} of {total.toLocaleString("en-IN")} shown.</div>;
}

const JobCells = ({ r, link = true }) => (
  <>
    <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{link && r.id ? <JobLink id={r.id} number={r.bpr} newTab /> : r.bpr}</td>
    <td className="px-3 py-2 text-xs"><div className="truncate max-w-[11rem]" title={r.customer_name}>{r.customer_name}</div></td>
    <td className="px-3 py-2 text-xs"><div className="truncate max-w-[16rem]" title={r.product_name}>{r.product_name}</div></td>
  </>
);

const Head = ({ cols }) => (
  <thead className="bg-neutral-50 label-caps text-[10px]">
    <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
  </thead>
);

function Frame({ children, shown, total }) {
  return (
    <div className="border border-neutral-200">
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full text-sm">{children}</table>
      </div>
      <Shown shown={shown} total={total} />
    </div>
  );
}

function ChangesTable({ rows, total }) {
  return (
    <Frame shown={rows.length} total={total}>
      <Head cols={["BPR", "Customer", "Product", "Changes"]} />
      <tbody>
        {rows.map((r) => (
          <tr key={r.bpr} className="border-t border-neutral-100 align-top" data-testid={`plan-change-${r.bpr}`}>
            <JobCells r={r} />
            <td className="px-3 py-2 text-xs space-y-0.5">
              {r.changes.map((x) => (
                <div key={x.field}>
                  <span className="text-neutral-500">{label(x.field)}:</span>{" "}
                  <span className="text-neutral-400 line-through">{fmt(x.erp)}</span>{" → "}
                  <span className="font-medium text-neutral-900">{fmt(x.excel)}</span>
                </div>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </Frame>
  );
}

function NewTable({ rows, total }) {
  return (
    <Frame shown={rows.length} total={total}>
      <Head cols={["BPR", "Customer", "Product", "PO", "Qty", "Status"]} />
      <tbody>
        {rows.map((r) => (
          <tr key={r.bpr} className="border-t border-neutral-100">
            <JobCells r={r} link={false} />
            <td className="px-3 py-2 font-mono-num text-xs">{r.po_number || "—"}</td>
            <td className="px-3 py-2 font-mono-num text-xs text-right">{fmt(r.quantity)}</td>
            <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
          </tr>
        ))}
      </tbody>
    </Frame>
  );
}

function KeptTable({ rows }) {
  return (
    <Frame shown={rows.length} total={rows.length}>
      <Head cols={["BPR", "Customer", "Product", "Field", "In the ERP (kept)", "In Excel", "Why"]} />
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.bpr}-${r.field}`} className="border-t border-neutral-100 align-top">
            <JobCells r={r} />
            <td className="px-3 py-2 text-xs whitespace-nowrap">{label(r.field)}</td>
            <td className="px-3 py-2 text-xs font-medium"><div className="max-w-[14rem] break-words">{fmt(r.erp)}</div></td>
            <td className="px-3 py-2 text-xs text-neutral-500"><div className="max-w-[14rem] break-words">{fmt(r.excel)}</div></td>
            <td className="px-3 py-2 text-[11px] text-neutral-500">{r.why}</td>
          </tr>
        ))}
      </tbody>
    </Frame>
  );
}

function ClashTable({ rows, total }) {
  return (
    <>
      <p className="text-xs text-neutral-600">
        These BPR numbers belong to a different product in the ERP than in the sheet, so the rows were not merged.
        Correct the BPR number in the sheet (or the job in the ERP) and import again.
      </p>
      <Frame shown={rows.length} total={total}>
        <Head cols={["BPR", "Customer", "In the ERP", "In the sheet"]} />
        <tbody>
          {rows.map((r) => (
            <tr key={r.bpr} className="border-t border-neutral-100 align-top">
              <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap"><JobLink id={r.id} number={r.bpr} newTab /></td>
              <td className="px-3 py-2 text-xs">{r.customer_name}</td>
              <td className="px-3 py-2 text-xs"><span className="font-mono-num">{r.product_code}</span> · {r.product_name}</td>
              <td className="px-3 py-2 text-xs text-rose-800"><span className="font-mono-num">{r.excel_product_code}</span> · {r.excel_product}</td>
            </tr>
          ))}
        </tbody>
      </Frame>
    </>
  );
}

function SkippedTable({ skipped, total }) {
  return (
    <>
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(skipped.by_reason).map(([reason, n]) => (
          <span key={reason} className="px-2 py-1 border border-neutral-200 rounded-sm">
            {reason} · <b className="font-mono-num">{n.toLocaleString("en-IN")}</b>
          </span>
        ))}
      </div>
      <p className="text-xs text-neutral-500">
        Rows without a BPR are usually inquiries not yet converted; they are picked up once the sheet has their BPR number.
      </p>
      <Frame shown={skipped.rows.length} total={total}>
        <Head cols={["Sheet", "Row", "Value in the BPR column", "Reason"]} />
        <tbody>
          {skipped.rows.map((s, i) => (
            <tr key={`${s.sheet}-${s.row}-${i}`} className="border-t border-neutral-100">
              <td className="px-3 py-1.5 text-xs">{s.sheet}</td>
              <td className="px-3 py-1.5 text-xs font-mono-num">{s.row}</td>
              <td className="px-3 py-1.5 text-xs font-mono-num">{s.value || "—"}</td>
              <td className="px-3 py-1.5 text-xs text-neutral-600">{s.reason}</td>
            </tr>
          ))}
        </tbody>
      </Frame>
    </>
  );
}
